import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { BATTERY_SIZING_STATUS } from '../../constants/battery-sizing.constants';
import { Project, ProjectSizingToolResult } from '../../models';
import { ManualSizing } from '../../models/manual-sizing.model';

@Component({
  selector: 'app-selected-configuration-summary',
  imports: [NzTypographyModule],
  templateUrl: './selected-configuration-summary.component.html',
  styleUrl: './selected-configuration-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class SelectedConfigurationSummaryComponent {
  readonly project = input<Project | null>(null);

  private readonly selectedSizingResult =
    computed<ProjectSizingToolResult | null>(() => {
      const results = this.project()?.sizingToolResults ?? [];
      return (
        results.find(
          (result) => result.resultStatus === BATTERY_SIZING_STATUS.Selected,
        ) ?? null
      );
    });

  private readonly manualResult = computed<ManualSizing | null>(() => {
    if (!this.project()?.isManualSizing) {
      return null;
    }
    return this.project()?.manualSizing ?? null;
  });

  readonly isManualSizing = computed(
    () => this.project()?.isManualSizing ?? false,
  );

  readonly rangeCode = computed(
    () => this.selectedSizingResult()?.battery_range?.code ?? '-',
  );

  readonly rangeName = computed(
    () => this.selectedSizingResult()?.battery_range?.name ?? '',
  );

  readonly rangeLabel = computed(() => {
    const code = this.rangeCode();
    const name = this.rangeName();
    return name ? `${code} - ${name}` : code;
  });

  readonly batteryName = computed(() => {
    if (this.isManualSizing()) {
      return this.manualResult()?.battery?.partNumber ?? '-';
    }
    return (
      this.selectedSizingResult()?.configuration?.battery?.partNumber ?? '-'
    );
  });

  readonly manualConfigNumber = computed(
    () => this.manualResult()?.configNumber ?? '-',
  );

  readonly manualStrings = computed(() => this.manualResult()?.string ?? '-');

  readonly manualQuantity = computed(
    () => this.manualResult()?.quantity ?? '-',
  );

  readonly manualTotal = computed(() => this.manualResult()?.total ?? '-');

  readonly parallelSeries = computed(() => {
    if (this.isManualSizing()) {
      return `${this.manualConfigNumber()} x ${this.manualStrings()}`;
    }

    const config = this.selectedSizingResult()?.configuration;
    if (config?.parallels === undefined && config?.seriesBlocks === undefined) {
      return '-';
    }

    const parallels = config?.parallels !== undefined ? config.parallels : '-';
    const seriesBlocks =
      config?.seriesBlocks !== undefined
        ? `${config.seriesBlocks} blocks`
        : '-';
    return `${parallels} x ${seriesBlocks}`;
  });

  readonly c10OutputLabelAh = computed(
    () => this.selectedSizingResult()?.configuration?.c10OutputLabelAh ?? '-',
  );

  readonly realBackupMinutes = computed(
    () => this.selectedSizingResult()?.configuration?.realBackupMinutes ?? '-',
  );
}
